import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Phone } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import { useAuth } from '../../contexts/AuthContext';
import forgotPasswordImage from '../../assets/images/services-1.png';
import tecnvironsLogo from '../../assets/images/Tecnvirons-logo.jpeg';
import 'react-phone-input-2/lib/style.css';
import '../common/phone-input-overrides.css';
import './ForgotPasswordModal.css';

const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin, onResetEmailSent, onResetPassword, userEmail, userType = 'user', clientId = null }) => {
  const {
    forgotPassword,
    adminForgotPassword,
    rbacForgotPassword,
    adminRbacForgotPassword
  } = useAuth();
  const [authType, setAuthType] = useState('email');
  const [email, setEmail] = useState(userEmail || '');
  const [phone, setPhone] = useState('');
  const [method, setMethod] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isRestricted, setIsRestricted] = useState(false);
  const [backendAuthType, setBackendAuthType] = useState(null);

  React.useEffect(() => {
    if (isOpen && userEmail) {
      setEmail(userEmail);
    }
  }, [isOpen, userEmail]);

  React.useEffect(() => {
    if (!isOpen) return undefined;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'email') {
      setEmail(value);
      if (error) setError('');
    }
  };

  const handlePhoneChange = (value) => {
    setPhone(value);
    if (error) setError('');
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits.startsWith('91')) {
      setMethod('');
    }
  };

  const validateInput = (input, type) => {
    if (type === 'email') {
      return /\S+@\S+\.\S+/.test(input);
    }
    return /^[0-9\-+()\s]{10,}$/.test(input.replace(/\s+/g, ''));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const input = authType === 'email' ? email : phone;
    const inputLabel = authType === 'email' ? 'email address' : 'phone number';

    if (!input) {
      setError(`Please enter your ${inputLabel}`);
      return;
    }

    if (!validateInput(input, authType)) {
      setError(`Please enter a valid ${inputLabel}`);
      return;
    }

    const normalizedPhone = (phone || '').replace(/\D/g, '');
    const isIndian = normalizedPhone.startsWith('91');

    if (authType === 'phone' && isIndian && !method) {
      setError('Please select SMS or WhatsApp delivery method');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      let payload;
      if (authType === 'email') {
        payload = input;
      } else {
        const selectedMethod = method || (isIndian ? '' : 'whatsapp');
        payload = { phone: input, method: selectedMethod };
      }

      let result;
      if (userType === 'admin') {
        result = await adminForgotPassword(payload, authType);
      } else if (userType === 'rbac') {
        const rbacPayload = authType === 'email'
          ? { email: input, client_id: clientId }
          : { phone: input, client_id: clientId, method: payload.method };
        result = await rbacForgotPassword(rbacPayload);
      } else if (userType === 'admin-rbac') {
        const adminRbacPayload = { email: input, admin_id: clientId };
        result = await adminRbacForgotPassword(adminRbacPayload);
      } else {
        result = await forgotPassword(payload, authType);
      }

      if (result.success) {
        setSuccess(result.message);
        if (result.authType) {
          setBackendAuthType(result.authType);
        } else {
          setBackendAuthType(authType);
        }

        if (result.restricted) {
          setIsRestricted(true);
          setIsEmailSent(true);
        } else {
          if (onResetEmailSent) {
            onResetEmailSent(input, result.authType || authType);
          }
          setIsEmailSent(true);
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setEmail('');
    setPhone('');
    setMethod('');
    setIsEmailSent(false);
    setError('');
    setSuccess('');
    setBackendAuthType(null);
    onBackToLogin && onBackToLogin();
  };

  const handleResetPassword = () => {
    const effectiveAuthType = backendAuthType || authType;
    const input = effectiveAuthType === 'email' ? email : phone;
    if (onResetPassword) {
      onResetPassword(input, effectiveAuthType);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="forgot-password-modal-overlay" onClick={onClose}>
      <div
        className="forgot-password-modal-container auth-modal"
        onClick={e => e.stopPropagation()}
        style={{ '--modal-bg-image': `url(${forgotPasswordImage})` }}
      >
        <button className="modal-close-btn" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>

        <div className="modal-form-section">
          <div className="form-content" data-lenis-prevent>
            <div className="modal-header">
              <div className="modal-logo">
                <img src={tecnvironsLogo} alt="Tecnvirons" className="modal-logo-image" />
              </div>
            </div>

            <div className="form-main">
              {!isEmailSent ? (
                <>
                  <h2 className="form-title">Forgot Your Password?</h2>
                  <p className="form-subtitle">
                    No worries! Enter your email address and we'll send you a reset code.
                  </p>

                  {error && <div className="error-message">{error}</div>}

                  <form onSubmit={handleSubmit} className="forgot-password-form">
                    {userType !== 'rbac' && (
                      <div className="auth-type-toggle" style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '20px',
                        borderBottom: '2px solid #e0e0e0',
                        paddingBottom: '10px'
                      }}>
                        <button
                          type="button"
                          onClick={() => {
                            setAuthType('email');
                            setPhone('');
                            setMethod('');
                            setError('');
                          }}
                          style={{
                            flex: 1,
                            padding: '10px',
                            border: 'none',
                            background: authType === 'email' ? '#7c3aed' : '#f3f4f6',
                            color: authType === 'email' ? 'white' : '#6b7280',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          Email
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setAuthType('phone');
                            setEmail('');
                            setMethod('');
                            setError('');
                          }}
                          style={{
                            flex: 1,
                            padding: '10px',
                            border: 'none',
                            background: authType === 'phone' ? '#7c3aed' : '#f3f4f6',
                            color: authType === 'phone' ? 'white' : '#6b7280',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <Phone width={16} height={16} style={{ display: 'inline', marginRight: '5px' }} />
                          Phone
                        </button>
                      </div>
                    )}

                    {authType === 'email' ? (
                      <div className="modal-form-group">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          placeholder=" "
                          value={email}
                          onChange={handleInputChange}
                          disabled={isLoading}
                          required
                          autoFocus
                        />
                        <label htmlFor="email">Email Address</label>
                      </div>
                    ) : (
                      <>
                        <div className="modal-form-group modal-form-group-phone">
                          <label htmlFor="phone" style={{ display: 'block', marginBottom: '8px', color: '#4b5563', fontSize: '14px' }}>Phone Number</label>
                          <PhoneInput
                            value={phone}
                            onChange={handlePhoneChange}
                            country="in"
                            countryCodeEditable={true}
                            disableDropdown={false}
                            enableSearch={true}
                            preferredCountries={['in', 'us', 'gb', 'ae', 'sa', 'pk', 'bd']}
                            searchPlaceholder="Search country..."
                            searchNotFound="No country found"
                            disabled={isLoading}
                            autoFocus
                          />
                        </div>

                        {phone && phone.replace(/\D/g, '').startsWith('91') && (
                          <div style={{
                            marginBottom: '15px',
                            padding: '12px',
                            backgroundColor: '#f5f3ff',
                            borderRadius: '8px',
                            border: '1px solid #e0e7ff'
                          }}>
                            <p style={{ fontSize: '0.85rem', color: '#4338ca', marginBottom: '10px', fontWeight: '600' }}>
                              Select OTP delivery method:
                            </p>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={() => setMethod('sms')}
                                disabled={isLoading}
                                style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  border: '2px solid ' + (method === 'sms' ? '#7c3aed' : '#e5e7eb'),
                                  background: method === 'sms' ? '#f3e8ff' : '#fafafa',
                                  color: method === 'sms' ? '#7c3aed' : '#6b7280',
                                  borderRadius: '6px',
                                  cursor: isLoading ? 'not-allowed' : 'pointer',
                                  fontWeight: '600',
                                  fontSize: '0.85rem'
                                }}
                              >
                                SMS
                              </button>
                              <button
                                type="button"
                                onClick={() => setMethod('whatsapp')}
                                disabled={isLoading}
                                style={{
                                  flex: 1,
                                  padding: '8px 12px',
                                  border: '2px solid ' + (method === 'whatsapp' ? '#7c3aed' : '#e5e7eb'),
                                  background: method === 'whatsapp' ? '#f3e8ff' : '#fafafa',
                                  color: method === 'whatsapp' ? '#7c3aed' : '#6b7280',
                                  borderRadius: '6px',
                                  cursor: isLoading ? 'not-allowed' : 'pointer',
                                  fontWeight: '600',
                                  fontSize: '0.85rem'
                                }}
                              >
                                WhatsApp
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    <button
                      type="submit"
                      className="submit-btn"
                      disabled={isLoading || (!email && !phone)}
                    >
                      {isLoading ? 'Sending...' : 'Send Reset Code'}
                    </button>
                  </form>

                  <div className="form-footer">
                    <p>
                      Remember your password?{' '}
                      <button type="button" className="link-btn" onClick={handleBackToLogin}>
                        Back to Login
                      </button>
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="form-title">{authType === 'email' ? 'Check Your Email' : 'Check Your Phone'}</h2>
                  <p className="form-subtitle">
                    {isRestricted
                      ? 'Security measures have been triggered for this role.'
                      : `We've sent a password reset code to ${authType === 'email' ? email : phone}`}
                  </p>

                  {success && <div className="success-message">{success}</div>}

                  <div className="success-content">
                    <div className="success-icon" style={isRestricted ? { background: '#f59e0b' } : undefined}>
                      {isRestricted ? (
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                          <line x1="12" y1="9" x2="12" y2="13"></line>
                          <line x1="12" y1="17" x2="12.01" y2="17"></line>
                        </svg>
                      ) : (
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="m9 12 2 2 4-4"></path>
                        </svg>
                      )}
                    </div>

                    <div className="instructions">
                      <h4>{isRestricted ? 'Super Admin Notified' : "What's next?"}</h4>
                      <ul>
                        {isRestricted ? (
                          <>
                            <li>A notification has been sent to your account's Super Admin</li>
                            <li>They will review your request and reset your password manually</li>
                          </>
                        ) : (
                          <>
                            <li>Check your {authType === 'email' ? 'email inbox' : 'phone'} for the reset code</li>
                            <li>The code will expire in 15 minutes</li>
                            {authType === 'email' && <li>Don't forget to check your spam folder</li>}
                          </>
                        )}
                      </ul>
                    </div>

                    {!isRestricted && (
                      <button
                        type="button"
                        className="submit-btn"
                        onClick={handleResetPassword}
                        style={{ marginTop: '20px', width: '100%' }}
                      >
                        I have the code — Enter Reset Code
                      </button>
                    )}

                    <div className="form-footer">
                      <p>
                        Didn't receive the {authType === 'email' ? 'email' : 'code'}?{' '}
                        <button
                          type="button"
                          className="link-btn"
                          onClick={() => {
                            setIsEmailSent(false);
                            setSuccess('');
                          }}
                        >
                          Try Again
                        </button>
                      </p>
                      <p>
                        <button type="button" className="link-btn" onClick={handleBackToLogin}>
                          Back to Login
                        </button>
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="modal-image-section">
          <img src={forgotPasswordImage} alt="Forgot Password" className="modal-image" />
          <div className="image-overlay">
            <div className="decorative-shape top-left-shape"></div>
            <div className="decorative-shape bottom-right-shape"></div>

            <div className="overlay-content">
              <div className="overlay-card">
                <h3>Reset Made Easy!</h3>
                <p>We'll help you get back into your account quickly and securely.</p>
              </div>
              <div className="bottom-overlay">
                <h3>Secure Password Recovery,<br />Your Way!</h3>
                <p>Account security is our priority.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ForgotPasswordModal;
